import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import TimeEntryStatusIcon from '@atom/components/common/timeEntryStatusIcon/TimeEntryStatusIcon';
import TeamContext from '@atom/components/teamPortal/TeamContext';
import { TIME_ENTRY_DELETE, TIME_ENTRY_UPDATE } from '@atom/graph/timeEntry';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Icon, Menu, Modal } from '@atom/mui';
import { DeleteIcon, EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { PolicyAction } from '@atom/types/policy';
import { TimeEntryDetail, TimeEntryStatus } from '@atom/types/timeEntry';
import { hasAccess } from '@atom/utilities/accessUtilities';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  CLOSED_STATUS_ID,
  IN_PROGRESS_STATUS_ID,
} from '@atom/utilities/workOrderStatusUtilities';

const MenuItem = Menu.MenuItem;

const styles = {
  rejectButton: {
    background: colors.brand.red,
    color: colors.neutral.white,
  },
  modal: {
    padding: '1rem',
  },
};

interface Props {
  timeEntry: TimeEntryDetail;
  disabled: boolean;
  setEditTimeEntryId: (id: string) => void;
  refetchTimeSheet: () => void;
}

const TimeEntryOptions = ({
  timeEntry,
  disabled,
  setEditTimeEntryId,
  refetchTimeSheet,
}: Props) => {
  const userProfile = useUserProfile();
  const [{ timeSheetUserId }] = useNavigation({
    timeSheetUserId: SearchParamType.STRING,
  });

  const { timeEntryApproverInfo } = useContext(TeamContext);

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [rejectOpen, setRejectOpen] = useState<boolean>(false);

  const [timeEntryDelete, { loading: timeEntryDeleteLoading }] =
    useMutation(TIME_ENTRY_DELETE);

  const [timeEntryUpdate, { loading: timeEntryRejectLoading }] =
    useMutation(TIME_ENTRY_UPDATE);

  const [updateWorkDetail, { loading: workDetailUpdateLoading }] =
    useMutation(WORK_ORDER_UPDATE);

  const availableActions = R.pathOr(
    [],
    ['actions'],
    R.find(R.propEq(timeEntry?.userGroupId, 'id'), timeEntryApproverInfo),
  );

  const canRejectPending = hasAccess(
    PolicyAction.REJECT_PENDING,
    availableActions,
  );
  const canRejectApproved = hasAccess(
    PolicyAction.REJECT_APPROVED,
    availableActions,
  );

  const canReject =
    (timeEntry.status === TimeEntryStatus.pending && canRejectPending) ||
    (timeEntry.status === TimeEntryStatus.approved && canRejectApproved);

  const showReject =
    timeEntry.status !== TimeEntryStatus.draft &&
    timeEntry.status !== TimeEntryStatus.rejected &&
    canReject &&
    timeSheetUserId;

  const disableAction =
    timeEntry.workOrder.statusId === CLOSED_STATUS_ID ||
    timeEntry.status === TimeEntryStatus.approved ||
    (timeEntry.userId !== userProfile.userId &&
      doesNotHaveRolePermissions(ROLE_SETS.MANAGER));

  const handleDelete = async () => {
    await timeEntryDelete({
      variables: {
        id: timeEntry.id,
      },
    });

    refetchTimeSheet();
    setDeleteOpen(false);
  };

  const handleReject = async () => {
    await timeEntryUpdate({
      variables: {
        input: {
          id: timeEntry.id,
          status: TimeEntryStatus.rejected,
        },
      },
    });

    if (timeEntry?.workOrder?.statusId === CLOSED_STATUS_ID) {
      await updateWorkDetail({
        variables: {
          workOrder: {
            id: timeEntry.workOrder.id,
            statusId: IN_PROGRESS_STATUS_ID,
          },
        },
      });
    }

    refetchTimeSheet();
    setRejectOpen(false);
  };

  const rejectContent =
    timeEntry?.workOrder?.statusId === CLOSED_STATUS_ID
      ? 'Are you sure you want to reject this time entry? Rejecting the time entry will reopen work.'
      : ' Are you sure you want to reject this time entry?';

  const rejectModalLoading = timeEntryRejectLoading || workDetailUpdateLoading;

  return (
    <>
      <Menu
        noMinWidth
        disabled={disabled}
        IconButtonProps={{ size: 'small' }}
        data-cy="timeEntryMenu"
      >
        {showReject && (
          <MenuItem
            startAdornment={
              <TimeEntryStatusIcon status={TimeEntryStatus.rejected} />
            }
            onClick={() => setRejectOpen(true)}
            data-cy="timeEntryRejectButton"
          >
            Reject
          </MenuItem>
        )}
        <MenuItem
          startAdornment={<Icon icon={EditIcon} />}
          onClick={() => setEditTimeEntryId(timeEntry.id)}
          disabled={disableAction}
          data-cy="timeEntryEditButton"
        >
          Edit
        </MenuItem>
        <MenuItem
          startAdornment={<Icon icon={DeleteIcon} />}
          onClick={() => setDeleteOpen(true)}
          disabled={disableAction}
          data-cy="timeEntryDeleteButton"
        >
          Delete
        </MenuItem>
      </Menu>
      <Modal
        title="Reject Time Entry"
        onConfirm={handleReject}
        onCancel={() => setRejectOpen(false)}
        confirmButtonText="Reject"
        open={rejectOpen}
        disabled={!showReject}
        loading={rejectModalLoading}
        contentStyle={styles.modal}
        ConfirmButtonProps={{ sx: styles.rejectButton }}
        data-cy="timeEntryRejectModal"
      >
        {rejectContent}
      </Modal>
      <DeleteModal
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Time Entry"
        content={<div>Are you sure you want to delete this time entry?</div>}
        loading={timeEntryDeleteLoading}
        data-cy="timeEntryDeleteModal"
      />
    </>
  );
};

export default TimeEntryOptions;
